import { useEffect, useState } from "react";
import axios from "axios";
import { Ticket, Users, X, CheckCircle2, MessageCircle, Layers } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const inputCls = "w-full px-3 py-2.5 bg-purple-900/40 border border-purple-700/50 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-yellow-500";

export const TorneoRegistration = ({ torneo, user }) => {
  const [data, setData] = useState({ inscritos: [], total: 0, cupo: null, disponibles: null });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nombre: user?.name || "", telefono: "", email: user?.email || "", deck: "", equipo: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const load = () => axios.get(`${API}/torneos/${torneo.id}/inscritos`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [torneo.id]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const r = await axios.post(`${API}/torneos/${torneo.id}/inscribir`, form);
      setDone(r.data); load();
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo completar la inscripción");
    }
    setSaving(false);
  };

  const isOpen = torneo.status === "proximo";
  const full = data.cupo && data.total >= data.cupo;
  const pct = data.cupo ? Math.min(100, Math.round((data.total / data.cupo) * 100)) : null;

  return (
    <div className="mb-8" data-testid="torneo-registration">
      <div className="p-4 rounded-2xl glass-card border border-purple-500/30">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div>
            <p className="text-[11px] tracking-widest text-gray-400">INSCRIPCIÓN {isOpen ? "ABIERTA" : "CERRADA"}</p>
            <p className="text-white font-bold flex items-center gap-2"><Users size={16} className="text-pink-400" /> <span data-testid="torneo-inscritos-count">{data.total}</span> inscrito{data.total !== 1 && "s"}{data.cupo && <span className="text-gray-400 font-normal"> · {data.disponibles} cupos libres de {data.cupo}</span>}</p>
          </div>
          {isOpen && (
            <button onClick={() => setOpen(true)} disabled={full} className="px-5 py-3 rounded-xl bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-black hover:brightness-110 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="torneo-register-btn">
              <Ticket size={18} /> {full ? "Cupo lleno" : "Inscribirme"}
            </button>
          )}
        </div>
        {pct !== null && <div className="h-2 rounded-full bg-black/50 overflow-hidden mb-3"><div className="h-full bg-gradient-to-r from-pink-500 to-yellow-400 transition-all duration-700" style={{ width: `${pct}%` }} /></div>}
        {data.inscritos.length > 0 && (
          <div className="flex flex-wrap gap-2 max-w-full" data-testid="torneo-inscritos-list">
            {data.inscritos.map((i) => (
              <span key={i.id} className="px-2.5 py-1 rounded-full bg-purple-900/50 border border-purple-700/50 text-xs text-gray-200 flex items-center gap-1.5 max-w-full truncate">
                <span className="w-5 h-5 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 text-[10px] font-bold flex items-center justify-center text-white">{i.nombre.charAt(0)}</span>
                {i.nombre}{i.deck && <span className="text-purple-300 flex items-center gap-0.5"><Layers size={10} />{i.deck}</span>}
              </span>
            ))}
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="torneo-register-modal">
          <div className="glass-card rounded-2xl p-6 max-w-md w-full border border-purple-500/40">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-black text-yellow-400">Inscripción · {torneo.name}</h3>
              <button onClick={() => { setOpen(false); setDone(null); setError(""); setForm({ nombre: user?.name || "", telefono: "", email: user?.email || "", deck: "", equipo: "" }); }} className="text-gray-400 hover:text-white" data-testid="torneo-register-close"><X /></button>
            </div>
            {done ? (
              <div className="text-center py-4" data-testid="torneo-register-success">
                <CheckCircle2 size={48} className="mx-auto text-emerald-400 mb-3" />
                <p className="text-white font-bold text-lg">¡Inscripción #{done.numero} confirmada!</p>
                <p className="text-gray-400 text-sm mt-1 mb-5">Te esperamos el {torneo.date}{torneo.time && ` a las ${torneo.time}`}. Confirma tu asistencia con la tienda por WhatsApp.</p>
                {done.whatsapp_url && <a href={done.whatsapp_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-emerald-400 to-green-500 text-black font-black" data-testid="torneo-register-whatsapp"><MessageCircle size={18} /> Confirmar por WhatsApp</a>}
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <input required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Nombre y apellido *" className={inputCls} data-testid="reg-nombre" />
                <input required value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="Teléfono (ej: 04121234567) *" className={inputCls} data-testid="reg-telefono" />
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (opcional)" className={inputCls} data-testid="reg-email" />
                <div className="grid grid-cols-2 gap-3">
                  <input value={form.deck} onChange={(e) => setForm({ ...form, deck: e.target.value })} placeholder="Deck" className={inputCls} data-testid="reg-deck" />
                  <input value={form.equipo} onChange={(e) => setForm({ ...form, equipo: e.target.value })} placeholder="Equipo" className={inputCls} data-testid="reg-equipo" />
                </div>
                {error && <p className="text-red-400 text-sm" data-testid="reg-error">{error}</p>}
                <button type="submit" disabled={saving} className="w-full py-3 rounded-xl bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-black disabled:opacity-50" data-testid="reg-submit">{saving ? "Enviando..." : "Confirmar inscripción"}</button>
                <p className="text-[11px] text-gray-500 text-center">Entrada: {torneo.entry_fee || "consultar"} · Se paga en la tienda el día del evento.</p>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
